import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RegisterForm } from '@/components/auth';
import { authService } from '@/services';
import type { RegisterCredentials } from '@/types';

const RegisterPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleRegister = async (credentials: RegisterCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.signUp(credentials);
      setSuccess(true);
      // Navigation will be handled by AuthGuard/router
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
            <p className="text-gray-600 mb-6">
              Your account has been created successfully. You can now start chatting!
            </p>
            <Link 
              to="/chat" 
              className="btn-primary"
            >
              Go to Chat
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Chat App</h1>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Create your account</h2>
          <p className="text-gray-600">Join our community and start chatting</p>
        </div>

        {/* Register Form */}
        <div className="card max-w-md mx-auto">
          <RegisterForm 
            onSubmit={handleRegister}
            isLoading={isLoading}
            error={error}
          />
          
          {/* Sign in link */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            By creating an account, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;